import { useCallback, useEffect, useState, type FormEvent } from "react";

import { DocumentUploadPanel } from "@/components/applications/DocumentUploadPanel";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/lib/api/client";
import { scrollToFormError } from "@/lib/scroll-to-form-error";
import {
  missingRequiredDocumentLabels,
  validateBusinessKycForm,
} from "@/lib/partner-validations";
import {
  buildChecklist,
  listApplicationDocuments,
  listDocumentMappings,
} from "@/services/documents";
import {
  createBusinessKyc,
  getKycOverview,
  getKycReference,
  updateBusinessKyc,
  type BusinessKyc,
  type BusinessKycPayload,
  type KycReferenceData,
} from "@/services/kyc";
import type { DocumentChecklistItem } from "@/types/application";

type BusinessKycPanelProps = {
  applicationUuid: string;
  /** True after credit is approved — business KYC add/edit is blocked. */
  locked?: boolean;
  /** Prefill Business Name / Industry Sector from lead details. */
  defaultCompanyName?: string | null;
  defaultIndustrySectorId?: string | number | null;
  onDocumentsChanged?: () => Promise<void> | void;
};

type FormState = {
  name: string;
  address: string;
  city: string;
  postal_code: string;
  business_type: string;
  business_segment: string;
  industry_sector: string;
  identity_type: string;
  identity: string;
};

const emptyForm = (): FormState => ({
  name: "",
  address: "",
  city: "",
  postal_code: "",
  business_type: "",
  business_segment: "",
  industry_sector: "",
  identity_type: "TRADE_LICENSE_NO",
  identity: "",
});

function formFromKyc(
  kyc: BusinessKyc,
  defaults?: { companyName?: string; industrySectorId?: string },
): FormState {
  return {
    name: kyc.name || defaults?.companyName || "",
    address: kyc.address || "",
    city: kyc.city != null ? String(kyc.city) : "",
    postal_code: kyc.postal_code || "",
    business_type: kyc.business_type != null ? String(kyc.business_type) : "",
    business_segment: kyc.business_segment != null ? String(kyc.business_segment) : "",
    industry_sector:
      kyc.industry_sector != null
        ? String(kyc.industry_sector)
        : defaults?.industrySectorId || "",
    identity_type: kyc.identity_type || "TRADE_LICENSE_NO",
    identity: kyc.identity || "",
  };
}

function formFromLeadDefaults(defaults: {
  companyName?: string;
  industrySectorId?: string;
}): FormState {
  return {
    ...emptyForm(),
    name: defaults.companyName || "",
    industry_sector: defaults.industrySectorId || "",
  };
}

function toPayload(form: FormState, allowedIdentityTypes: string[]): BusinessKycPayload {
  const toId = (value: string) => (value ? Number(value) : null);
  // Older business KYC rows carry the model default "CR_NUMBER", which isn't a
  // valid business identity type — the select can't show it (so the user sees
  // the first option), and echoing it back fails validation. Send what's shown.
  const identityType = allowedIdentityTypes.includes(form.identity_type)
    ? form.identity_type
    : allowedIdentityTypes[0] || "TRADE_LICENSE_NO";
  return {
    name: form.name.trim(),
    address: form.address.trim() || undefined,
    city: toId(form.city),
    postal_code: form.postal_code.trim() || undefined,
    business_type: toId(form.business_type),
    business_segment: toId(form.business_segment),
    industry_sector: toId(form.industry_sector),
    identity_type: identityType,
    identity: form.identity.trim() || undefined,
  };
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted">{label}</div>
      <p className="mt-1 text-sm text-ink">{value || "—"}</p>
    </div>
  );
}

export function BusinessKycPanel({
  applicationUuid,
  locked = false,
  defaultCompanyName,
  defaultIndustrySectorId,
  onDocumentsChanged,
}: BusinessKycPanelProps) {
  const toast = useToast();
  const [kyc, setKyc] = useState<BusinessKyc | null>(null);
  const [reference, setReference] = useState<KycReferenceData | null>(null);
  const [checklist, setChecklist] = useState<DocumentChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const leadDefaults = {
    companyName: (defaultCompanyName || "").trim(),
    industrySectorId:
      defaultIndustrySectorId != null && String(defaultIndustrySectorId).trim()
        ? String(defaultIndustrySectorId)
        : "",
  };

  const loadDocuments = useCallback(
    async (
      businessTypeId: number | null | undefined,
      opts?: { silent?: boolean },
    ) => {
      if (!businessTypeId) {
        setChecklist([]);
        setDocsError(null);
        setDocsLoading(false);
        return;
      }
      if (!opts?.silent) {
        setDocsLoading(true);
        setChecklist([]);
      }
      setDocsError(null);
      try {
        const [mappings, documents] = await Promise.all([
          listDocumentMappings({
            business_type_id: businessTypeId,
            category: "business_kyc",
          }),
          listApplicationDocuments(applicationUuid),
        ]);
        setChecklist(buildChecklist(mappings, documents));
      } catch (err) {
        setChecklist([]);
        setDocsError(
          err instanceof ApiError
            ? err.message
            : "Unable to load business document checklist",
        );
      } finally {
        if (!opts?.silent) setDocsLoading(false);
      }
    },
    [applicationUuid],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overview, ref] = await Promise.all([
        getKycOverview(applicationUuid),
        getKycReference(),
      ]);
      setKyc(overview.business_kyc);
      setReference(ref);
      if (overview.business_kyc) {
        setForm(formFromKyc(overview.business_kyc, leadDefaults));
        if (overview.business_kyc.status === "verified") {
          setEditing(false);
          setCreating(false);
        }
        await loadDocuments(overview.business_kyc.business_type);
      } else {
        setChecklist([]);
        setForm(formFromLeadDefaults(leadDefaults));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to load business KYC");
    } finally {
      setLoading(false);
    }
  }, [
    applicationUuid,
    loadDocuments,
    leadDefaults.companyName,
    leadDefaults.industrySectorId,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!locked) return;
    setEditing(false);
    setCreating(false);
  }, [locked]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (locked) return;
    const nextErrors = validateBusinessKycForm(form);
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setError("Please complete all required business KYC fields.");
      scrollToFormError();
      return;
    }
    if (docsLoading) {
      const message = "Please wait for the document checklist to finish loading.";
      setError(message);
      toast.error(message);
      scrollToFormError();
      return;
    }
    const missingDocs = missingRequiredDocumentLabels(checklist);
    if (missingDocs.length) {
      const message = `Upload required documents before saving: ${missingDocs.join(", ")}.`;
      setError(message);
      toast.error(message);
      scrollToFormError();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = toPayload(
        form,
        reference?.identity_types.map((o) => o.value) ?? [],
      );
      const saved = kyc
        ? await updateBusinessKyc(kyc.uuid, payload)
        : await createBusinessKyc(applicationUuid, payload);
      setKyc(saved);
      setForm(formFromKyc(saved, leadDefaults));
      setFieldErrors({});
      setEditing(false);
      setCreating(false);
      await loadDocuments(saved.business_type, { silent: true });
      await onDocumentsChanged?.();
    } catch (err) {
      if (err instanceof ApiError) {
        const next: Record<string, string> = {};
        for (const [key, value] of Object.entries(err.fieldErrors)) {
          next[key] = Array.isArray(value) ? value[0] : value;
        }
        setFieldErrors(next);
        const message = err.message || "Unable to save business KYC";
        setError(message);
        toast.error(message);
        scrollToFormError();
      } else {
        const message = "Unable to save business KYC";
        setError(message);
        toast.error(message);
        scrollToFormError();
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-7" />
      </div>
    );
  }

  const isVerified = Boolean(kyc && kyc.status === "verified");
  const canEdit = !locked && (!kyc || (Boolean(kyc.can_edit) && !isVerified));
  const showForm = canEdit && (creating || editing);
  const docsReadOnly = locked || isVerified || !showForm;
  const activeBusinessTypeId = showForm
    ? form.business_type
      ? Number(form.business_type)
      : null
    : (kyc?.business_type ?? null);

  const documentsBlock = (
    <div className="border-t border-line pt-6">
      {docsError ? <Alert className="mb-4">{docsError}</Alert> : null}
      {docsLoading ? (
        <div className="flex justify-center py-10">
          <Spinner className="size-6" />
        </div>
      ) : (
        <DocumentUploadPanel
          applicationUuid={applicationUuid}
          checklist={checklist}
          businessTypeId={activeBusinessTypeId}
          readOnly={docsReadOnly}
          embedded
          title="Business documents"
          description={
            locked
              ? "Documents are locked because credit has been approved."
              : isVerified
                ? "Documents are locked because this business KYC is verified."
                : docsReadOnly
                  ? undefined
                  : "Required documents depend on business type (Trade License, Ejari/Title Deed, and type-specific docs)."
          }
          emptyMessage={
            activeBusinessTypeId
              ? "No document mappings for this business type."
              : "Select a business type above, then the document list will appear here."
          }
          onUploaded={async () => {
            await loadDocuments(activeBusinessTypeId, { silent: true });
            await onDocumentsChanged?.();
          }}
        />
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-line bg-surface-raised p-5 sm:p-6">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ink">Business KYC</h2>
            <p className="mt-1 text-sm text-muted">
              {locked
                ? "Business KYC is read-only after credit has been approved."
                : isVerified
                  ? "This business KYC is verified and can no longer be edited."
                  : showForm
                    ? "Enter business KYC details and upload documents, then save."
                    : "Click Edit to make changes."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {kyc ? (
              <span className="rounded-full bg-surface px-3 py-1 text-xs font-semibold text-ink ring-1 ring-line">
                {kyc.status_display}
              </span>
            ) : null}
            {kyc && canEdit && !showForm ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setForm(formFromKyc(kyc, leadDefaults));
                  setEditing(true);
                  setError(null);
                  setFieldErrors({});
                }}
              >
                Edit KYC
              </Button>
            ) : null}
            {!kyc && !showForm ? (
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setForm(formFromLeadDefaults(leadDefaults));
                  setCreating(true);
                  setError(null);
                  setFieldErrors({});
                }}
              >
                + Add Business KYC
              </Button>
            ) : null}
          </div>
        </div>

        {locked ? (
          <Alert className="mb-4" tone="success">
            Business KYC cannot be added or edited after credit has been approved.
          </Alert>
        ) : null}

        {error ? <Alert className="mb-4">{error}</Alert> : null}

        {!kyc && !showForm ? (
          <p className="text-sm text-muted">No business KYC on file.</p>
        ) : null}

        {kyc && !showForm ? (
          <div className="space-y-8">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <InfoCard
                label="Business name"
                value={kyc.name || leadDefaults.companyName || "—"}
              />
              <InfoCard label="Type" value={kyc.business_type_name || "—"} />
              <InfoCard label="KYC status" value={kyc.status_display} />
              <InfoCard label="City" value={kyc.city_name || "—"} />
              <InfoCard label="Postal code" value={kyc.postal_code || "—"} />
              <InfoCard label="Segment" value={kyc.business_segment_name || "—"} />
              <InfoCard
                label="Industry sector"
                value={
                  kyc.industry_sector_name ||
                  (leadDefaults.industrySectorId
                    ? reference?.industry_sectors.find(
                      (s) => String(s.id) === leadDefaults.industrySectorId,
                    )?.name
                    : null) ||
                  "—"
                }
              />
              <InfoCard label="Identity" value={kyc.identity || "—"} />
              <div className="sm:col-span-2 lg:col-span-3">
                <InfoCard label="Address" value={kyc.address || "—"} />
              </div>
            </div>
            {documentsBlock}
          </div>
        ) : null}

        {showForm && reference ? (
          <form onSubmit={onSubmit} className="space-y-8" noValidate>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Input
                  label="Business name *"
                  name="name"
                  value={form.name}
                  error={fieldErrors.name}
                  onChange={(e) => setField("name", e.target.value)}
                />
              </div>
              <Select
                label="Business type *"
                name="business_type"
                value={form.business_type}
                error={fieldErrors.business_type}
                onChange={(e) => {
                  const next = e.target.value;
                  setField("business_type", next);
                  void loadDocuments(next ? Number(next) : null);
                }}
                options={[
                  { value: "", label: "Select type" },
                  ...reference.business_types.map((o) => ({
                    value: String(o.id),
                    label: o.name,
                  })),
                ]}
              />
              <Select
                label="Business segment *"
                name="business_segment"
                value={form.business_segment}
                error={fieldErrors.business_segment}
                onChange={(e) => setField("business_segment", e.target.value)}
                options={[
                  { value: "", label: "Select segment" },
                  ...reference.business_segments.map((o) => ({
                    value: String(o.id),
                    label: o.name,
                  })),
                ]}
              />
              <Select
                label="Industry sector *"
                name="industry_sector"
                value={form.industry_sector}
                error={fieldErrors.industry_sector}
                onChange={(e) => setField("industry_sector", e.target.value)}
                options={[
                  { value: "", label: "Select sector" },
                  ...reference.industry_sectors.map((o) => ({
                    value: String(o.id),
                    label: o.name,
                  })),
                ]}
              />
              <Select
                label="City *"
                name="city"
                value={form.city}
                error={fieldErrors.city}
                onChange={(e) => setField("city", e.target.value)}
                options={[
                  { value: "", label: "Select city" },
                  ...reference.cities.map((o) => ({
                    value: String(o.id),
                    label: o.name,
                  })),
                ]}
              />
              <Input
                label="PO Box (Postal code) *"
                name="postal_code"
                value={form.postal_code}
                error={fieldErrors.postal_code}
                onChange={(e) => setField("postal_code", e.target.value)}
              />
              <Select
                label="Identity type"
                name="identity_type"
                value={form.identity_type}
                onChange={(e) => setField("identity_type", e.target.value)}
                options={reference.identity_types.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
              <Input
                label="Trade license number *"
                name="identity"
                value={form.identity}
                error={fieldErrors.identity}
                onChange={(e) => setField("identity", e.target.value)}
              />
              <div className="sm:col-span-2">
                <Input
                  label="Company address *"
                  name="address"
                  value={form.address}
                  error={fieldErrors.address}
                  onChange={(e) => setField("address", e.target.value)}
                />
              </div>
            </div>

            {documentsBlock}

            <div className="flex justify-end gap-2 border-t border-line pt-6">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setCreating(false);
                  setEditing(false);
                  setError(null);
                  setFieldErrors({});
                  if (kyc) setForm(formFromKyc(kyc, leadDefaults));
                  else setForm(formFromLeadDefaults(leadDefaults));
                }}
              >
                Cancel
              </Button>
              <Button type="submit" loading={saving}>
                {kyc ? "Save KYC" : "Create KYC"}
              </Button>
            </div>
          </form>
        ) : null}
      </section>
    </div>
  );
}
